import { locales } from "../lib/locales";

/**
 * Map a browser/system locale to one of our supported locales
 * Matches the iOS implementation logic for consistent behavior across platforms
 *
 * @param detectedLocale - The locale detected from the browser/system (e.g., "en-US", "pt-BR", "fil", "tl")
 * @returns A supported locale string (e.g., "en", "pt", "en-PH")
 *
 * @example
 * ```ts
 * mapToSupportedLocale("en-US") // "en"
 * mapToSupportedLocale("en-PH") // "en-PH" (exact match + region match)
 * mapToSupportedLocale("fil") // "en-PH" (Filipino language code)
 * mapToSupportedLocale("tl-PH") // "en-PH" (Tagalog)
 * mapToSupportedLocale("pt-BR") // "pt"
 * mapToSupportedLocale("ja-JP") // "en" (fallback)
 * ```
 */
export const mapToSupportedLocale = (detectedLocale: string): string => {
  if (!detectedLocale) return "en";

  const supportedLocales = [...locales];

  // 1. Check for exact match first
  if (supportedLocales.includes(detectedLocale as any)) {
    return detectedLocale;
  }

  // 2. Find all locales that match the language pattern
  const languageMatches = supportedLocales.filter((locale) =>
    localeMatches(detectedLocale, locale),
  );

  // 3. If there are matches, check for region-specific matches
  if (languageMatches.length > 0) {
    const regionCode = detectedLocale.split("-")[1]?.toUpperCase();

    if (regionCode) {
      // Prefer locale with matching region (e.g., "en-PH" for "en-PH" or "fil-PH")
      const regionMatch = languageMatches.find((locale) =>
        getMatchingRegions(locale).includes(regionCode),
      );

      if (regionMatch) {
        return regionMatch;
      }
    }

    // Return the first language match
    return languageMatches[0];
  }

  // 4. Default to English
  return "en";
};

/**
 * Check if a user locale matches a supported locale's pattern
 */
function localeMatches(userLocale: string, supportedLocale: string): boolean {
  const pattern = getRegexPattern(supportedLocale);
  if (!pattern) return false;

  const regex = new RegExp(pattern, "i"); // case insensitive
  return regex.test(userLocale);
}

/**
 * Get regex pattern for each supported locale
 */
function getRegexPattern(locale: string): string {
  switch (locale) {
    case "en":
      return "^en-[a-z]{2}$|^en$";
    case "es":
      return "^es-[a-z]{2}$|^es$";
    case "pt":
      return "^pt-[a-z]{2}$|^pt$";
    case "pl":
      return "^pl-[a-z]{2}$|^pl$";
    case "de":
      return "^de-[a-z]{2}$|^de$";
    case "en-PH":
      // Match en-PH, Filipino (fil), Tagalog (tl, tgl)
      return "^en-PH$|^fil.*|^tl.*|^tgl.*";
    case "fr":
      return "^fr-[a-z]{2}$|^fr$";
    case "it":
      return "^it-[a-z]{2}$|^it$";
    default:
      return "";
  }
}

/**
 * Get matching regions for each locale
 * Used to prioritize region-specific locales (e.g., en-PH for Philippines)
 */
function getMatchingRegions(locale: string): string[] {
  switch (locale) {
    case "en-PH":
      return ["PH"];
    default:
      return [];
  }
}
